import { CurrentUserResponse, QuickSwitchUser } from 'api/models/Users';
import { cacheSaviAuthToken } from 'utils/saviAuthTokenStorage';
import ApiClientBase from './ApiClientBase';

class UsersEndpoint extends ApiClientBase {
  apiUri = '/api/v2/users';

  async getCurrentUser(auth0UserId: string) {
    const user = await this.apiCall<CurrentUserResponse>('/current-user/');

    // save the storage key for the firebase token
    // NOTE it would be nice to optionally return the token in the response
    const storageKey = `firebase-exchange-token-${btoa(auth0UserId)}`;
    localStorage.setItem(storageKey, user.firebaseToken);

    // cache the short-lived savi auth token used to tokenise direct BE links (e.g. PDF downloads)
    cacheSaviAuthToken(auth0UserId, user.saviAuthToken);

    // assure legacy field: remove this when all imps are updated
    if (user.tenant?.name) {
      user.tenantName = user.tenant.name;
      user.tenantType = user.tenant.type;

      user.tenant.subscriptionId ||= 't1';
    } else {
      user.tenant = {
        id: '',
        name: user.tenantName,
        description: '',
        type: user.tenantType,
        subscriptionId: 't1',
      };
    }

    return user;
  }

  // generic endpoint backing all whitelisted location settings, see LOCATION_SETTINGS_WHITELIST in savi-api
  updateLocationSetting(key: string, value: string | number | boolean) {
    return this.apiCall('/update-location-setting/', 'put', {
      key,
      value,
    });
  }

  updateTenantUpdated(updated: string) {
    return this.apiCall('/tenant-updated/', 'put', {
      updated,
    });
  }

  // only succeeds when the caller's location has the quick switcher setting enabled
  listQuickSwitchUsers() {
    return this.apiCall<QuickSwitchUser[]>('/quick-switch-users/');
  }

  // keystore fallback path, used only when direct Firebase Storage access is gcpNetworkBlocked
  uploadKeystoreFile(gs: string, data: string) {
    return this.apiCall('/keystore/upload-file/', 'put', { gs, data });
  }

  downloadKeystoreFile(gs: string) {
    return this.apiCall<ArrayBuffer>(`/keystore/download-file/?gs=${encodeURIComponent(gs)}`, 'get', undefined, {
      responseType: 'arraybuffer',
    });
  }

  deleteKeystoreFile(gs: string) {
    return this.apiCall(`/keystore/delete-file/?gs=${encodeURIComponent(gs)}`, 'delete');
  }
}

const users = new UsersEndpoint();
export default users;
